'use client'

import classnames from 'classnames'
import React from 'react'
import {type OmitStrict} from 'type-zoo'

type ChildrenFunc = (hovered: boolean) => React.ReactNode | React.ReactNode[]

export type HoverTrackingDivProps = OmitStrict<
  React.HTMLAttributes<HTMLDivElement>,
  'children'
> & {
  children: React.ReactNode | React.ReactNode[] | ChildrenFunc

  /**
   * CSS class to apply to the div when hovered.
   */
  hoveredClassName?: string

  divRef?: React.Ref<HTMLDivElement>
  onHoverChange?: (hovered: boolean) => void
  /* Should focus also trigger the hover behavior? */
  focusable?: boolean
}

type IState = {
  hovered: boolean
  focused: boolean
}

/** A div that tells its children if it's hovered or not via a render prop. */
class HoverTrackingDivInternal extends React.Component<
  HoverTrackingDivProps,
  IState
> {
  state: IState = {
    hovered: false,
    focused: false,
  }

  componentWillUnmount() {
    this.props.onHoverChange?.(false)
  }

  render() {
    const {
      className,
      hoveredClassName,
      children,
      divRef,
      onHoverChange,
      focusable,
      ...divProps
    } = this.props
    const shouldAppearHovered = this.state.hovered || this.state.focused

    return (
      <div
        {...divProps}
        ref={divRef}
        className={classnames(className, shouldAppearHovered && hoveredClassName)}
        onPointerEnter={e => {
          this.setState({hovered: true}, () => onHoverChange?.(true))
          this.props.onPointerEnter?.(e)
        }}
        onPointerLeave={e => {
          this.setState({hovered: false}, () => onHoverChange?.(false))
          this.props.onPointerLeave?.(e)
        }}
        onFocus={e => {
          if (focusable) {
            this.setState({focused: true}, () => onHoverChange?.(true))
          }
          this.props.onFocus?.(e)
        }}
        onBlur={e => {
          if (focusable) {
            this.setState({focused: false}, () => onHoverChange?.(false))
          }
          this.props.onBlur?.(e)
        }}
        tabIndex={focusable ? 0 : undefined}
      >
        {typeof children === 'function' ? children(shouldAppearHovered) : children}
      </div>
    )
  }
}

const HoverTrackingDiv = React.forwardRef<HTMLDivElement, HoverTrackingDivProps>(
  (props, ref) => <HoverTrackingDivInternal divRef={ref} {...props} />
)
export default HoverTrackingDiv
